import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { IGetDishesShortByRestaurantId } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { getDishesShortByRestaurantId } from 'backend/dishes';
import { MultipleSelect } from './MultipleSelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import style from './DishMultipleSelect.module.scss';

interface IProps {
	setSelectedDishes: (value: string[]) => void;
	selectedDishes: string[];
	selectedRestaurant: Nullable<string>;
	restaurantIsLoading: boolean;
}

export const DishMultipleSelect: React.FC<IProps> = React.memo(
	({ setSelectedDishes, selectedDishes, selectedRestaurant, restaurantIsLoading }) => {
		const [isFirstRender, setIsFirstRender] = useState(true);
		const { response, loading, refetch, responseError } = useFetchData<IGetDishesShortByRestaurantId, string>(
			selectedRestaurant ? getDishesShortByRestaurantId : null,
			selectedRestaurant || '',
		);

		useEffect(() => {
			if (!isFirstRender) {
				refetch();
			} else {
				setIsFirstRender(false);
			}
		}, [selectedRestaurant]);

		const handleRemove = useCallback(
			(id: string) => {
				const selectedItems = selectedDishes.filter((el) => el !== id);
				setSelectedDishes(selectedItems);
			},
			[setSelectedDishes, selectedDishes],
		);

		const dishesList = useMemo(() => {
			return selectedDishes.map((el) => {
				const label = response?.data.restaurantDishes.find((dish) => dish.id === el)?.name || '';
				return <SelectedItem key={el} label={label} onDelete={() => handleRemove(el)} />;
			});
		}, [handleRemove, selectedDishes, response?.data.restaurantDishes]);

		return (
			<div className={style.container} data-test="DishMultipleSelect">
				<MultipleSelect
					data={response?.data.restaurantDishes || []}
					isLoading={loading || restaurantIsLoading}
					refetch={refetch}
					responseError={responseError}
					setSelectedDishes={setSelectedDishes}
					selectedDishes={selectedDishes}
				/>
				<div className={style.groupsList}>{dishesList}</div>
			</div>
		);
	},
);
